/**
 * Tank registry PR 2 — Paelode manages an operator company's tank registry on its behalf
 * (/v1/platform/operator-companies/:tenantId/tanks, /tank-tags, /batteries, /wells and
 * /tanks/import; routes/platform/operator-company-tanks.ts). The same actions as the operator
 * admin's Tank setup in the portal, for the company in the path. MANAGE_OPERATOR_PORTAL_ACCESS.
 *
 * A write whose path carries an id (edit a tank, its tags, a battery) takes the id when the hook
 * is made, like api-key-hooks.ts: each row renders its own hook. Every write refreshes the whole
 * family (tanks, batteries, wells) so counts and placements stay in step.
 */
import { useQueryClient } from '@tanstack/react-query';
import {
  authedFetch,
  useApiMutation,
  useApiOpts,
  useApiQuery,
  type EmptyBody,
} from '@lodex-pro/web-shared';
import type {
  AddTankTagRequest,
  BatteryView,
  CompanyWellOption,
  RegistryTankView,
  SaveBatteryRequest,
  SaveRegistryTankRequest,
  SetBatteryWellsRequest,
  TankImportResponse,
} from '@lodex-pro/shared/validation/operator-portal-routes';

export const OPERATOR_TANK_REGISTRY_KEY = (tenantId: string): readonly string[] => [
  'platform',
  'operator-company-tanks',
  tenantId,
];

const base = (tenantId: string): string => `/v1/platform/operator-companies/${tenantId}`;

// ── Reads ────────────────────────────────────────────────────────────────────

export function useCompanyTanks(
  tenantId: string,
): ReturnType<typeof useApiQuery<{ tanks: RegistryTankView[] }>> {
  const opts = useApiOpts();
  return useApiQuery<{ tanks: RegistryTankView[] }>(opts, `${base(tenantId)}/tanks`, {
    queryKey: [...OPERATOR_TANK_REGISTRY_KEY(tenantId), 'tanks'],
    staleTime: 15_000,
  });
}

export function useCompanyBatteries(
  tenantId: string,
): ReturnType<typeof useApiQuery<{ batteries: BatteryView[] }>> {
  const opts = useApiOpts();
  return useApiQuery<{ batteries: BatteryView[] }>(opts, `${base(tenantId)}/batteries`, {
    queryKey: [...OPERATOR_TANK_REGISTRY_KEY(tenantId), 'batteries'],
    staleTime: 15_000,
  });
}

/** The company's catalog wells, for the well pickers. */
export function useCompanyWellOptions(
  tenantId: string,
): ReturnType<typeof useApiQuery<{ wells: CompanyWellOption[] }>> {
  const opts = useApiOpts();
  return useApiQuery<{ wells: CompanyWellOption[] }>(opts, `${base(tenantId)}/wells`, {
    queryKey: [...OPERATOR_TANK_REGISTRY_KEY(tenantId), 'wells'],
    staleTime: 60_000,
  });
}

/** Refetch the tanks, batteries and wells after a change. */
export function useRefreshCompanyTanks(tenantId: string): () => void {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: OPERATOR_TANK_REGISTRY_KEY(tenantId) });
}

// ── Writes ───────────────────────────────────────────────────────────────────

function useRegistryWrite<TBody, TRes>(
  tenantId: string,
  path: string,
  method: 'POST' | 'PUT' | 'PATCH',
): ReturnType<typeof useApiMutation<TBody, TRes>> {
  const opts = useApiOpts();
  const refresh = useRefreshCompanyTanks(tenantId);
  return useApiMutation<TBody, TRes>(opts, `${base(tenantId)}${path}`, method, {
    onSuccess: refresh,
  });
}

/** Add (tankId null: POST) or edit (PUT, the full record) one of the company's tanks. */
export function useSaveCompanyTank(
  tenantId: string,
  tankId: string | null,
): ReturnType<typeof useApiMutation<SaveRegistryTankRequest, { id: string }>> {
  return useRegistryWrite<SaveRegistryTankRequest, { id: string }>(
    tenantId,
    tankId === null ? '/tanks' : `/tanks/${tankId}`,
    tankId === null ? 'POST' : 'PUT',
  );
}

export function useAddCompanyTankTag(
  tenantId: string,
  tankId: string,
): ReturnType<typeof useApiMutation<AddTankTagRequest, { id: string }>> {
  return useRegistryWrite<AddTankTagRequest, { id: string }>(
    tenantId,
    `/tanks/${tankId}/tags`,
    'POST',
  );
}

export function useRemoveCompanyTankTag(
  tenantId: string,
  tagId: string,
): ReturnType<typeof useApiMutation<EmptyBody, { removed: boolean }>> {
  return useRegistryWrite<EmptyBody, { removed: boolean }>(
    tenantId,
    `/tank-tags/${tagId}/remove`,
    'POST',
  );
}

/** Add (batteryId null: POST) or rename / (de)activate (PATCH) a battery. */
export function useSaveCompanyBattery(
  tenantId: string,
  batteryId: string | null,
): ReturnType<typeof useApiMutation<SaveBatteryRequest, { id: string }>> {
  return useRegistryWrite<SaveBatteryRequest, { id: string }>(
    tenantId,
    batteryId === null ? '/batteries' : `/batteries/${batteryId}`,
    batteryId === null ? 'POST' : 'PATCH',
  );
}

export function useSetCompanyBatteryWells(
  tenantId: string,
  batteryId: string,
): ReturnType<typeof useApiMutation<SetBatteryWellsRequest, { wellCount: number }>> {
  return useRegistryWrite<SetBatteryWellsRequest, { wellCount: number }>(
    tenantId,
    `/batteries/${batteryId}/wells`,
    'PUT',
  );
}

// ── Import (CSV / XLSX) ──────────────────────────────────────────────────────

/** Why the server refused the whole file, from its VALIDATION_FAILED fields, else a generic line. */
async function importErrorMessage(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as {
      error?: { code?: string; fields?: Array<{ path?: string; reason?: string }> };
    };
    const reason = body.error?.fields?.find((f) => f.path === 'file')?.reason;
    if (reason !== undefined) return reason;
    if (body.error?.code === 'PAYLOAD_TOO_LARGE')
      return 'That file is too large. The limit is 2 MB.';
  } catch {
    // Not JSON; fall through.
  }
  return 'The file could not be read. Use a CSV or XLSX with a tank name column and a well API number, site or battery column.';
}

/**
 * Upload the file for a preview (apply false) or to write it (apply true). One multipart `file`
 * part; FormData, so the browser sets the content type. The server re-reads the file and the
 * registry on apply; nothing from the preview is trusted.
 */
export async function importCompanyTanks(
  opts: ReturnType<typeof useApiOpts>,
  tenantId: string,
  file: File,
  apply: boolean,
): Promise<TankImportResponse> {
  const form = new FormData();
  form.append('file', file, file.name);
  const res = await authedFetch(
    opts,
    `${base(tenantId)}/tanks/import${apply ? '?apply=true' : ''}`,
    { method: 'POST', body: form },
  );
  if (!res.ok) throw new Error(await importErrorMessage(res));
  return (await res.json()) as TankImportResponse;
}

/** A refused registry write, in words. */
export function tankRegistryErrorMessage(err: unknown): string {
  const code = typeof err === 'object' && err !== null ? (err as { code?: unknown }).code : null;
  switch (code) {
    case 'OPERATOR_TANK_NAME_TAKEN':
      // 13.8 (D-22): names are unique per placement, not across the company.
      return 'Another active tank on this well, site or battery already has that name.';
    case 'OPERATOR_TANK_PRODUCER_ID_TAKEN':
      return 'Another active tank already has this producer tank ID';
    case 'OPERATOR_TANK_TAG_TAKEN':
      return 'Another of this company’s tanks already has that tag in that system.';
    case 'OPERATOR_BATTERY_NAME_TAKEN':
      return 'Another of this company’s batteries already has that name.';
    case 'OPERATOR_BATTERY_WELL_TAKEN':
      return 'That well is already on another of this company’s batteries.';
    case 'OPERATOR_WELL_NOT_OWNED':
    case 'OPERATOR_COMPANY_SCOPE_VIOLATION':
      return 'That well or battery is not this company’s.';
    case 'OPERATOR_TENANT_NOT_FOUND':
      return 'This operator company was not found, or it is inactive.';
    case 'VALIDATION_FAILED':
      return 'Check the fields: a name, exactly one of a well, site or battery, and sizes above 0.';
    default:
      return 'That did not work. Try again.';
  }
}
